import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  LinearProgress,
  Alert,
  AlertTitle
} from '@mui/material'
import {
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material'

interface SessionWarningProps {
  open: boolean
  timeRemaining: number // in seconds
  sessionTimeout: number // in minutes
  onExtend: () => void
  onLogout: () => void
  isExtending?: boolean
}

/**
 * SessionWarning Component
 * 
 * Displays a warning dialog when session is about to expire.
 * Shows time remaining with visual countdown and provides options
 * to extend session or log out immediately.
 */
export const SessionWarning: React.FC<SessionWarningProps> = ({
  open,
  timeRemaining,
  sessionTimeout,
  onExtend,
  onLogout,
  isExtending = false
}) => {
  // Format time remaining for display
  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    
    return `0:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Calculate progress percentage
  const progressPercentage = (timeRemaining / (sessionTimeout * 60)) * 100

  // Determine warning level based on time remaining
  const getWarningLevel = (): 'warning' | 'error' => {
    if (timeRemaining < 60) return 'error' // Less than 1 minute
    return 'warning'
  }

  const warningLevel = getWarningLevel()

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: (theme) => theme.shadows[8]
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 1
      }}>
        <WarningIcon color={warningLevel} />
        <Typography variant="h6" component="div">
          Session Expiration Warning
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Alert severity={warningLevel} sx={{ mb: 2 }}>
          <AlertTitle>Your session is about to expire!</AlertTitle>
          <Typography variant="body2">
            Due to inactivity, your session will expire in {formatTimeRemaining(timeRemaining)}.
            You can extend your session or log out now to secure your account.
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Time Remaining
            </Typography>
            <Typography variant="body2" fontWeight="bold" color={warningLevel === 'error' ? 'error.main' : 'warning.main'}>
              {formatTimeRemaining(timeRemaining)}
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            color={warningLevel === 'error' ? 'error' : 'warning'}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200'
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <ScheduleIcon fontSize="small" />
          <Typography variant="caption">
            Session timeout: {sessionTimeout} minutes of inactivity
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onLogout}
          color="secondary"
          variant="outlined"
        >
          Log Out Now
        </Button>
        
        <Button
          onClick={onExtend}
          variant="contained"
          disabled={isExtending}
          startIcon={
            isExtending ? 
              <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : 
              <RefreshIcon />
          }
          sx={{ minWidth: 140 }}
        >
          {isExtending ? 'Extending...' : 'Stay Logged In'}
        </Button>
      </DialogActions>

      {/* Add global styles for animation */}
      <Box
        sx={{
          '@keyframes spin': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' }
          }
        }}
      />
    </Dialog>
  )
}
